import { memo, type ReactNode } from "react";
import { useT } from "@transifex/react";
import { CheckCircleIcon, CheckIcon, XIcon, ChatCenteredIcon, WarningIcon, type Icon } from "@phosphor-icons/react";
import { ShareTestItem } from "p6m-p6u";
import { AreaButton, ButtonVariant } from "../teacherArea/AreaButton";
import { PhosphorIcon } from "../../basic/phosphorIcon/PhosphorIcon";
import PhaseSixIcon from "../../basic/phaseSixIcon/PhaseSixIcon";
import useBreakpoints, { Breakpoint } from "../../hooks/useBreakpoints";
import { PILL_BUTTON_CLASS, ORANGE_OUTLINE_BUTTON_CLASS } from "./styleConstants";

const CARD_SHELL_CLASS = "w-full bg-white border border-p6-grey-light rounded-[12px]";

const VerticalDivider = () => (
    <div
        className="self-stretch w-px bg-p6-grey-mid"
        aria-hidden="true"
    />
);

const DotDivider = () => (
    <div
        className="size-[6px] rounded-circle bg-p6-grey-mid shrink-0"
        aria-hidden="true"
    />
);

interface StudentResultCardProps {
    item: ShareTestItem;
    canShareInClassroom: boolean;
    onShareClick: (resultId: string, studentName: string) => void;
    onGradeClick: (resultId: string) => void;
    onCopyLink: (link: string) => void;
}

const StudentResultCard = (props: StudentResultCardProps) => {
    const t = useT();
    const { isAboveMD, getStyleBasedOnBreakpoint } = useBreakpoints();

    const item = props.item;
    const isShared = !!item.sharedInClassroomDate;
    // A student can be fully evaluated even when the share record failed to be created; keep those two
    // states separate so a network hiccup doesn't make a graded student look ungraded (red "% open").
    const isFullyEvaluated = item.isFullyEvaluated;
    const canCopyLink = item.canBeShared;
    const reviewPercNum = Math.max(0, Math.min(100, Number(item.reviewPerc) || 0));
    const openPercent = 100 - reviewPercNum;

    const t_evaluate = t("Evaluate", { _tags: "ShareTestReviews, StudentResultCard" });
    const t_share = t("Share evaluation", { _tags: "ShareTestReviews, StudentResultCard" });
    const t_sharedBadge = t("Shared in classroom", { _tags: "ShareTestReviews, StudentResultCard" });
    const t_linkPending = t("Result link available after evaluation is complete.", {
        _tags: "ShareTestReviews, StudentResultCard",
    });
    const t_sr_copyLink = t("Copy result link", {
        _tags: "ShareTestReviews, StudentResultCard, ScreenReaderOnly",
    });
    const t_sr_correct = t("Correct answers", {
        _tags: "ShareTestReviews, StudentResultCard, ScreenReaderOnly",
    });
    const t_sr_incorrect = t("Incorrect answers", {
        _tags: "ShareTestReviews, StudentResultCard, ScreenReaderOnly",
    });
    const t_sr_comments = t("Comments", {
        _tags: "ShareTestReviews, StudentResultCard, ScreenReaderOnly",
    });
    const t_openLabel = t("{percent}% open", {
        _tags: "ShareTestReviews, StudentResultCard",
        percent: openPercent,
    });
    const t_evaluatedLabel = t("{percent}% evaluated", {
        _tags: "ShareTestReviews, StudentResultCard",
        percent: reviewPercNum,
    });

    const renderTopAction = () => {
        if (!isFullyEvaluated) {
            return (
                <AreaButton
                    variant={ButtonVariant.SECONDARY}
                    role="button"
                    onClick={() => props.onGradeClick(item.resultId)}
                    className={ORANGE_OUTLINE_BUTTON_CLASS}
                >
                    {t_evaluate}
                </AreaButton>
            );
        }
        if (isShared) {
            return (
                <div className="flex items-center gap-xs text-m text-p6-green">
                    <PhosphorIcon
                        phosphorIcon={CheckIcon}
                        fontSize="text-xl"
                        color="text-p6-green"
                    />
                    <span>{t_sharedBadge}</span>
                </div>
            );
        }
        // No share action for legacy tests (no classroom mode), or when the share record never
        // persisted (withheld until a retry flips canBeShared back to true).
        if (!props.canShareInClassroom || !canCopyLink) {
            return null;
        }
        return (
            <AreaButton
                variant={ButtonVariant.SECONDARY}
                role="button"
                onClick={() => props.onShareClick(item.resultId, item.name)}
                className={PILL_BUTTON_CLASS}
            >
                {t_share}
            </AreaButton>
        );
    };

    const renderStatusLabel = () => {
        const isOpen = !isFullyEvaluated;
        const icon = isOpen ? WarningIcon : CheckCircleIcon;
        const color = isOpen ? "text-p6-red" : "text-p6-green";
        const label = isOpen ? t_openLabel : t_evaluatedLabel;
        return (
            <div
                className={`flex items-center ${getStyleBasedOnBreakpoint(
                    Breakpoint.MD,
                    "gap-xs",
                    "gap-xxs"
                )} text-l ${color} whitespace-nowrap`}
            >
                <PhosphorIcon
                    phosphorIcon={icon}
                    fontSize="text-xl"
                    color={color}
                    weight={isOpen ? undefined : "fill"}
                />
                <span>{label}</span>
            </div>
        );
    };

    const renderStat = (value: ReactNode, icon: Icon, label: string, cellWidthClass = "") => (
        <div
            className={`flex items-center justify-center gap-xs whitespace-nowrap ${cellWidthClass}`}
            aria-label={label}
        >
            <span>{value}</span>
            <PhosphorIcon
                phosphorIcon={icon}
                fontSize="text-l"
                color="text-p6-grey-dark"
            />
        </div>
    );

    const renderStats = () => {
        const Divider = isAboveMD ? VerticalDivider : DotDivider;
        // On desktop, give each percentage cell a fixed width so the "|" dividers line up across cards;
        // the value + icon are centered within the cell (see renderStat's justify-center).
        const cellWidth = isAboveMD ? "w-[70px]" : "";
        return (
            <div className="flex flex-wrap items-center gap-x-s gap-y-xs text-l text-p6-grey-dark max-w-full">
                {renderStat(`${item.correctPerc}%`, CheckIcon, t_sr_correct, cellWidth)}
                <Divider />
                {renderStat(`${item.incorrectPerc}%`, XIcon, t_sr_incorrect, cellWidth)}
                <Divider />
                {renderStat(item.totalComments, ChatCenteredIcon, t_sr_comments)}
            </div>
        );
    };

    const renderLinkArea = () => {
        if (!canCopyLink) {
            return <span className="text-s text-p6-grey-dark">{t_linkPending}</span>;
        }
        return (
            <button
                type="button"
                onClick={() => props.onCopyLink(item.shareLink)}
                className="group flex items-center gap-xs text-s text-p6-grey-dark hover:text-p6-orange cursor-pointer min-w-0 max-w-full text-left"
                title={item.shareLink}
                aria-label={t_sr_copyLink}
            >
                <span className={`min-w-0 flex-1 ${isAboveMD ? "truncate" : "break-all"}`}>{item.shareLink}</span>
                <PhaseSixIcon
                    name="duplicate"
                    className="text-l text-p6-grey-dark group-hover:text-p6-orange"
                />
            </button>
        );
    };

    if (!isAboveMD) {
        return (
            <div className={`${CARD_SHELL_CLASS} flex flex-col`}>
                <div className="flex flex-col gap-s p-m">
                    <div className="flex flex-col">
                        <h3 className="text-l text-p6-grey-dark font-normal break-words">{item.name}</h3>
                        {renderStatusLabel()}
                    </div>
                    {renderStats()}
                </div>
                <div className="flex flex-col gap-s p-m border-t border-p6-grey-light">
                    {renderLinkArea()}
                    {renderTopAction()}
                </div>
            </div>
        );
    }

    return (
        <div className={`${CARD_SHELL_CLASS} px-m py-s flex flex-col gap-xs`}>
            <div className="flex w-full items-center justify-between gap-s flex-row">
                <h3 className="text-l text-p6-grey-dark font-normal break-words">{item.name}</h3>
                <div className="shrink-0">{renderTopAction()}</div>
            </div>
            <div className="flex w-full flex-row items-center flex-wrap gap-s">
                <div className="flex flex-row items-center gap-s self-stretch">
                    <div className="w-[190px] shrink-0">{renderStatusLabel()}</div>
                    <VerticalDivider />
                    {renderStats()}
                </div>
                <div className="flex flex-1 min-w-0 justify-end">{renderLinkArea()}</div>
            </div>
        </div>
    );
};

export default memo(StudentResultCard);
